type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  module: string;
  message: string;
  data?: unknown;
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LOG_COLORS: Record<LogLevel, string> = {
  debug: 'color: #9e9e9e',
  info: 'color: #2196f3',
  warn: 'color: #ff9800',
  error: 'color: #f44336',
};

const CONSOLE_METHOD: Record<LogLevel, 'log' | 'info' | 'warn' | 'error'> = {
  debug: 'log',
  info: 'info',
  warn: 'warn',
  error: 'error',
};

class Logger {
  private static instance: Logger;
  private minLevel: LogLevel;

  private constructor() {
    this.minLevel = process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.minLevel];
  }

  log(level: LogLevel, module: string, message: string, data?: unknown): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      module,
      message,
      ...(data !== undefined && { data }),
    };

    const prefix = `%c[${entry.timestamp}] [${level.toUpperCase()}] [${module}]`;
    const method = CONSOLE_METHOD[level];
    const style = LOG_COLORS[level];

    if (data !== undefined) {
      console[method](prefix, style, message, data);
    } else {
      console[method](prefix, style, message);
    }
  }

  debug(module: string, message: string, data?: unknown): void {
    this.log('debug', module, message, data);
  }

  info(module: string, message: string, data?: unknown): void {
    this.log('info', module, message, data);
  }

  warn(module: string, message: string, data?: unknown): void {
    this.log('warn', module, message, data);
  }

  error(module: string, message: string, data?: unknown): void {
    this.log('error', module, message, data);
  }
}

export const logger = Logger.getInstance();

interface ScopedLogger {
  debug: (message: string, data?: unknown) => void;
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
}

export function createLogger(module: string): ScopedLogger {
  const instance = Logger.getInstance();
  return {
    debug: (message: string, data?: unknown) => instance.debug(module, message, data),
    info: (message: string, data?: unknown) => instance.info(module, message, data),
    warn: (message: string, data?: unknown) => instance.warn(module, message, data),
    error: (message: string, data?: unknown) => instance.error(module, message, data),
  };
}
